/**
 * "Most of your −$0.31 is the years ahead" (U2-2): when the look-through part of the score is most of it, say which change carries it. Built from the score's printed parts and the changed decision whose
 * look-through lines (the attribution row's `valueParts.terminalLines`) are largest on the score's own side; nothing here is typed per decision.
 */
import type { ContentCatalog, DraftView, RoundReport } from "@bsim/contracts";
import { familyOfPath, type DecisionFamily } from "@bsim/domain";
import { formatUsdPerShare } from "../format";
import { lowerFirst } from "../format/labels";
import { printedParts } from "../score/labels";
import { decisionLabel } from "./decomposition";
import { moreYears } from "./yearStory";

/** Under five cents a part is not a story. */
const MIN_USD = 0.05;

/** The decision (path) whose look-through lines add up to most, on the side the look-through part has, with that sum; null when no changed decision carries it. */
export function carrier(report: RoundReport, lookThrough: number): { readonly path: string; readonly v: number } | null {
  let best: { readonly path: string; readonly v: number } | null = null;
  for (const r of report.decisionAttribution?.rows ?? []) {
    if (typeof r.key === "string") continue;
    const lines = r.valueParts?.terminalLines;
    const v = lines === undefined ? 0 : Object.values(lines).reduce<number>((t, x) => t + (x ?? 0), 0);
    if (v * lookThrough > 0 && (best === null || Math.abs(v) > Math.abs(best.v))) best = { path: r.key.decision, v };
  }
  return best;
}

/** What a change does over the years ahead, in the words of its desk: capital is kept working, lending earns, the rest add or take away. */
export function aheadVerb(path: string, usd: number): string {
  const family: DecisionFamily = familyOfPath(path);
  if (family === "capital") return usd < 0 ? "keeps less capital working" : "keeps more capital working";
  if (family === "loans") return usd < 0 ? "earns less" : "earns more";
  return usd < 0 ? "costs more" : "saves more";
}

/** "Most of your −$0.31 is the two more years: your change to liquidity earns less each month it runs on, about −$0.60, more than the market's +$0.26 today." Null unless the years ahead are most of the score. */
export function yearsAheadSentence(report: RoundReport, catalog: ContentCatalog, draft: DraftView | null = null): string | null {
  const c = report.score.components;
  if (c === undefined) return null;
  const total = report.score.valueCreatedUsdPerShare;
  const parts = printedParts(c, total);
  const lt = parts.lookThrough, today = Math.round((parts.price + parts.cash) * 100) / 100;
  if (Math.abs(lt) < MIN_USD || lt * total <= 0 || Math.abs(lt) <= Math.abs(total) / 2 || Math.abs(lt) < Math.abs(today)) return null;
  const money = (v: number): string => formatUsdPerShare(v, { signed: true });
  const years = moreYears(report);
  const top = carrier(report, lt);
  const why = top === null ? `your plan, run on, earns ${lt < 0 ? "less" : "more"} than your twin\u2019s` : `your change to ${lowerFirst(decisionLabel(top.path, catalog, draft))} ${aheadVerb(top.path, lt)}, about ${money(lt)}`;
  // This year's part, on the legs the hero prints beside it: the share price level and the cash returned.
  const against = Math.abs(today) < 0.005 ? "" : `, ${Math.abs(lt) > Math.abs(today) && lt * today < 0 ? "more than" : lt * today < 0 ? "against" : "beside"} this year\u2019s ${money(today)} (price ${money(parts.price)}, cash ${money(parts.cash)})`;
  return `Most of your ${money(total)} is the next ${years.replace(/ more /, " ")}, if the plan runs on: ${why}${against}.`;
}
